import React from 'react';
import { DndContext, DragOverlay, closestCenter } from '@dnd-kit/core';
import { Button } from '../../../components/ui/button';
import {
  Archive, Plus, Edit, Trash2, Layers, GripVertical,
  ArrowRightLeft, Eye, TrendingUp, History, ArrowUpDown,
  CheckSquare, Square
} from 'lucide-react';
import { T } from './constants';
import { DndPosition, DragGhost } from './BinsHelpers';

export const CabinetView = ({
  selectedNode, selectNode, getLiveCabinetData, getLiveShelfData,
  sensors, handleDragStart, handleDragEnd, handleDragCancel,
  activeDragData, transferring,
  heatmapMode, setHeatmapMode, fullscreenCabinet, setFullscreenCabinet,
  highlightedBinId, bulkMode, bulkSelected, toggleBulkSelect, selectAllInShelf,
  loadPositionDetail, setShowAddShelfDialog, setShowAddPositionDialog,
  setShowEditCabinetDialog, setShowMoveDialog, setMoveData, setEditCabinetName,
  handleDeleteGroup, startStocktake, loadCabinetHistory,
  getHeatmapColor, getHeatmapBorder,
}) => {
  if (!selectedNode || selectedNode.type === 'plant') {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Archive className="w-16 h-16 mx-auto mb-4 opacity-20" style={{ color: T.textDim }} />
          <p className="text-sm font-medium" style={{ color: T.textMuted }}>Selectati un dulap din arbore</p>
          <p className="text-xs mt-1" style={{ color: T.textDim }}>Click pe un dulap pentru a vedea layout-ul</p>
        </div>
      </div>
    );
  }

  if (selectedNode.type === 'cabinet') {
    const cab = getLiveCabinetData();
    if (!cab || !cab.shelves) return <div className="flex items-center justify-center h-full"><p className="text-xs" style={{ color: T.textDim }}>Se incarca datele dulapului...</p></div>;
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-extrabold tracking-tight">{selectedNode.cabinetName}</h2>
              <p className="text-xs" style={{ color: T.textDim }}>{selectedNode.plantName} &middot; {cab.total_bins} pozitii &middot; {cab.occupied_bins} ocupate</p>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" className={`h-8 ${heatmapMode ? 'text-orange-400 bg-orange-500/10' : 'text-zinc-400 hover:text-orange-400 hover:bg-zinc-800'}`} onClick={() => setHeatmapMode(!heatmapMode)} data-testid="btn-heatmap" title="Heatmap">
                <TrendingUp className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Heatmap</span>
              </Button>
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-blue-400 hover:bg-zinc-800 h-8" onClick={() => setFullscreenCabinet(!fullscreenCabinet)} title="Fullscreen">
                <Eye className="w-3.5 h-3.5" />
              </Button>
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-yellow-400 hover:bg-zinc-800 h-8" onClick={() => startStocktake(selectedNode.cabinetName, selectedNode.plantId)} title="Inventar">
                <ArrowUpDown className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Inventar</span>
              </Button>
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-purple-400 hover:bg-zinc-800 h-8" onClick={() => loadCabinetHistory(selectedNode.cabinetName, selectedNode.plantId)} title="Istoric">
                <History className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Istoric</span>
              </Button>
              <div className="w-px h-6 my-auto" style={{ background: T.border }} />
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white hover:bg-zinc-800 h-8" onClick={() => { setEditCabinetName(selectedNode.cabinetName); setShowEditCabinetDialog(true); }}><Edit className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-green-400 hover:bg-zinc-800 h-8" onClick={() => setShowAddShelfDialog(true)}><Plus className="w-3.5 h-3.5" /></Button>
              <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8" onClick={() => handleDeleteGroup(selectedNode.cabinetName, null, selectedNode.plantId)}><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>

          {activeDragData && (
            <div className="p-2.5 rounded-lg border flex items-center gap-2 animate-in fade-in slide-in-from-top-2 duration-300" style={{ background: 'rgba(59,130,246,0.1)', borderColor: 'rgba(59,130,246,0.3)' }}>
              <GripVertical className="w-4 h-4 text-blue-400 animate-pulse" />
              <span className="text-xs text-blue-300">Trageti <strong className="text-blue-200">{activeDragData.position}</strong> ({activeDragData.stockCount} art.) pe pozitia dorita</span>
            </div>
          )}
          {transferring && (
            <div className="p-2.5 rounded-lg border flex items-center gap-2" style={{ background: 'rgba(249,115,22,0.1)', borderColor: 'rgba(249,115,22,0.3)' }}>
              <div className="w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-orange-300">Se transfera stocul...</span>
            </div>
          )}

          <div className="space-y-3" style={{ perspective: '1200px' }}>
            {[...cab.shelves].reverse().map((shelf, shelfIdx) => (
              <ShelfCard key={shelf.name} shelf={shelf} shelfIdx={shelfIdx} selectedNode={selectedNode} selectNode={selectNode}
                bulkMode={bulkMode} bulkSelected={bulkSelected} selectAllInShelf={selectAllInShelf} toggleBulkSelect={toggleBulkSelect}
                highlightedBinId={highlightedBinId} heatmapMode={heatmapMode} getHeatmapColor={getHeatmapColor} getHeatmapBorder={getHeatmapBorder}
                loadPositionDetail={loadPositionDetail} setShowAddPositionDialog={setShowAddPositionDialog}
                setShowMoveDialog={setShowMoveDialog} setMoveData={setMoveData} handleDeleteGroup={handleDeleteGroup} />
            ))}
          </div>
        </div>
        <DragOverlay dropAnimation={{ duration: 300, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>
          {activeDragData ? <DragGhost data={activeDragData} /> : null}
        </DragOverlay>
      </DndContext>
    );
  }

  if (selectedNode.type === 'shelf') {
    const shelf = getLiveShelfData();
    if (!shelf || !shelf.positions) return <div className="flex items-center justify-center h-full"><p className="text-xs" style={{ color: T.textDim }}>Se incarca...</p></div>;
    return (
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd} onDragCancel={handleDragCancel}>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-extrabold">{selectedNode.cabinetName} &rarr; {selectedNode.shelfName}</h2>
              <p className="text-xs" style={{ color: T.textDim }}>{shelf.total_bins} pozitii</p>
            </div>
            <div className="flex gap-1.5">
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-blue-400 hover:bg-zinc-800 h-8" onClick={() => { setMoveData({ target_cabinet: '' }); setShowMoveDialog(true); }}><ArrowRightLeft className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Muta Raft</span></Button>
              <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-green-400 hover:bg-zinc-800 h-8" onClick={() => setShowAddPositionDialog(true)}><Plus className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Pozitie</span></Button>
            </div>
          </div>
          <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3" style={{ perspective: '800px' }}>
            {shelf.positions.map((pos) => (
              <DndPosition key={pos.id} pos={pos} shelfName={selectedNode.shelfName} isHighlighted={highlightedBinId === pos.id}
                bulkMode={bulkMode} isBulkSelected={bulkSelected.has(pos.id)} onBulkToggle={() => toggleBulkSelect(pos.id)}
                onSelect={() => { selectNode('position', { ...selectedNode, positionId: pos.id, positionName: pos.position, code: pos.code, data: pos }); loadPositionDetail(pos.id); }} />
            ))}
          </div>
        </div>
        <DragOverlay dropAnimation={{ duration: 300, easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)' }}>{activeDragData ? <DragGhost data={activeDragData} /> : null}</DragOverlay>
      </DndContext>
    );
  }

  return (
    <div className="flex items-center justify-center h-full">
      <p className="text-xs" style={{ color: T.textDim }}>Selectati un dulap sau raft</p>
    </div>
  );
};

const ShelfCard = ({ shelf, shelfIdx, selectedNode, selectNode, bulkMode, bulkSelected, selectAllInShelf, toggleBulkSelect, highlightedBinId, getHeatmapColor, getHeatmapBorder, loadPositionDetail, setShowAddPositionDialog, setShowMoveDialog, setMoveData, handleDeleteGroup }) => (
  <div className="rounded-xl border overflow-hidden transition-all duration-300 hover:shadow-[0_0_25px_rgba(59,130,246,0.08)]" style={{ background: T.paper, borderColor: T.border, transform: `rotateX(${shelfIdx === 0 ? 1 : 0}deg)`, transformOrigin: 'center top' }}>
    <div className="flex items-center justify-between px-4 py-2.5 border-b" style={{ borderColor: T.border, background: T.subtle }}>
      <div className="flex items-center gap-2">
        <Layers className="w-4 h-4 text-green-500" />
        <span className="text-sm font-bold">{shelf.name}</span>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: T.bg, color: T.textDim }}>{shelf.positions.length} poz</span>
      </div>
      <div className="flex items-center gap-2">
        {bulkMode && (
          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px] text-orange-400 hover:bg-orange-500/10"
            onClick={() => selectAllInShelf(shelf.positions)}
            data-testid={`bulk-select-shelf-${shelf.name}`}>
            {shelf.positions.every(p => bulkSelected.has(p.id)) ? <CheckSquare className="w-3 h-3" /> : <Square className="w-3 h-3" />}
          </Button>
        )}
        {shelf.occupied_bins > 0 && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">{shelf.occupied_bins} occ</span>}
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-zinc-500 hover:text-green-400" onClick={() => { selectNode('shelf', { ...selectedNode, shelfName: shelf.name, data: shelf }); setShowAddPositionDialog(true); }}><Plus className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-zinc-500 hover:text-blue-400" onClick={() => { selectNode('shelf', { ...selectedNode, shelfName: shelf.name, data: shelf }); setMoveData({ target_cabinet: '' }); setShowMoveDialog(true); }}><ArrowRightLeft className="w-3 h-3" /></Button>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-zinc-500 hover:text-red-400" onClick={() => handleDeleteGroup(selectedNode.cabinetName, shelf.name, selectedNode.plantId)}><Trash2 className="w-3 h-3" /></Button>
      </div>
    </div>
    <div className="p-3 grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
      {shelf.positions.map(pos => (
        <DndPosition key={pos.id} pos={pos} shelfName={shelf.name} isHighlighted={highlightedBinId === pos.id}
          heatmapBg={getHeatmapColor(pos)} heatmapBorder={getHeatmapBorder(pos)}
          bulkMode={bulkMode} isBulkSelected={bulkSelected.has(pos.id)} onBulkToggle={() => toggleBulkSelect(pos.id)}
          onSelect={() => { selectNode('position', { plantId: selectedNode.plantId, plantName: selectedNode.plantName, cabinetName: selectedNode.cabinetName, shelfName: shelf.name, positionId: pos.id, positionName: pos.position, code: pos.code, data: pos }); loadPositionDetail(pos.id); }} />
      ))}
    </div>
  </div>
);
